// Device capability detection. Everything here is synchronous, side-effect free
// and safe to call during SSR / tests (returns conservative defaults).

const QUALITIES = ["low", "medium", "high"];

export const isBrowser = typeof window !== "undefined";

export function prefersReducedMotion() {
  return isBrowser && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function hasCoarsePointer() {
  return isBrowser && window.matchMedia("(pointer: coarse)").matches;
}

export function hasFinePointer() {
  return isBrowser && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/**
 * Probe WebGL once. Returns { supported, software }.
 * `software` is true when the browser is rendering with a CPU rasteriser
 * (SwiftShader / llvmpipe / "Microsoft Basic Render Driver"), in which case we
 * drop to the lowest quality tier to keep the page usable.
 */
let glProbe;
export function probeWebGL() {
  if (glProbe) return glProbe;
  glProbe = { supported: false, software: false };
  if (!isBrowser) return glProbe;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    if (gl) {
      glProbe.supported = true;
      const ext = gl.getExtension("WEBGL_debug_renderer_info");
      const renderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : "";
      glProbe.software = /swiftshader|llvmpipe|software|basic render/i.test(renderer);
    }
  } catch {
    glProbe.supported = false;
  }
  return glProbe;
}

/**
 * low    -> phones, data-saver, <=2GB RAM, CPU rasteriser: no shadows, no AA, low DPR
 * medium -> tablets, small windows, modest laptops: soft shadows at 1024, DPR <= 1.5
 * high   -> desktop-class hardware: full quality
 * `?quality=low|medium|high` in the URL overrides detection (useful for testing).
 */
export function detectQuality() {
  if (!isBrowser) return "medium";
  const forced = new URLSearchParams(window.location.search).get("quality");
  if (QUALITIES.includes(forced)) return forced;

  const nav = navigator;
  const coarse = hasCoarsePointer();
  const phone = Math.min(window.innerWidth, window.innerHeight) < 600;
  const mem = nav.deviceMemory || 8;
  const cores = nav.hardwareConcurrency || 8;
  const saveData = Boolean(nav.connection && nav.connection.saveData);

  if (saveData || mem <= 2 || (coarse && phone) || probeWebGL().software) return "low";
  if (coarse || mem <= 4 || cores <= 4 || window.innerWidth < 820) return "medium";
  return "high";
}

export const QUALITY_PRESETS = {
  low: { dpr: [1, 1.25], antialias: false, shadows: false, shadowMap: 512, sparkles: 70, monoliths: 22, edge: 5, floaters: 7, fov: 52, curve: 4, bevel: 1, sphere: 24, knot: [90, 12] },
  medium: { dpr: [1, 1.5], antialias: true, shadows: true, shadowMap: 1024, sparkles: 160, monoliths: 34, edge: 7, floaters: 10, fov: 44, curve: 6, bevel: 3, sphere: 32, knot: [110, 16] },
  high: { dpr: [1, 1.5], antialias: true, shadows: true, shadowMap: 1024, sparkles: 200, monoliths: 46, edge: 10, floaters: 14, fov: 42, curve: 8, bevel: 4, sphere: 40, knot: [128, 20] },
};
